import { hashObjectIgnoreKeyOrder } from '@saulx/hash'
import {
  CSSProperties,
  forwardRef,
  memo,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Color, colors } from '../../tokens/colors.js'
import { Typography, TEXT_VARIANT_STYLES } from '../../tokens/typography.js'
import { styled, Style } from 'inlines'
import { Tooltip } from '../Tooltip/index.js'
import { mergeRefs } from '../../utils/mergeRefs.js'

type TextProps = {
  children: ReactNode
  variant?: Typography
  align?: 'left' | 'center' | 'right'
  color?: 'inherit' | Color
  maxWidth?: number
  singleLine?: boolean
  selectableText?: boolean
  style?: Style
}

const renderChildrenWithLineBreaks = (
  content: ReactNode,
  singleLine?: boolean,
) => {
  if (typeof content !== 'string') return content

  const text = singleLine ? content.replace(/\n/g, ' ') : content

  if (singleLine) {
    return text
  }

  const parts = text.split('\n')
  return parts.map((part, index) => (
    <styled.span key={index}>
      {part}
      {index < parts.length - 1 && <br />}
    </styled.span>
  ))
}

const Text = forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      children,
      variant = 'display-regular',
      color: colorProp = 'neutral100',
      align = 'left',
      maxWidth,
      singleLine,
      selectableText = true,
      style,
    },
    externalRef,
  ) => {
    const [tooltip, setTooltip] = useState<string>('')
    const innerRef = useRef<HTMLParagraphElement | null>(null)

    useEffect(() => {
      if (typeof children === 'string' && maxWidth !== undefined) {
        const rawText = singleLine ? children.replace(/\n/g, ' ') : children

        if (singleLine) {
          const width = measureTextWidth(rawText, variant)
          if (width > maxWidth) {
            setTooltip(rawText)
          } else {
            setTooltip('')
          }
        } else {
          const longestLineWidth = rawText.split('\n').reduce((max, line) => {
            const width = measureTextWidth(line, variant)
            return width > max ? width : max
          }, 0)

          if (longestLineWidth > maxWidth) {
            setTooltip(rawText)
          } else {
            setTooltip('')
          }
        }
      } else {
        setTooltip('')
      }
    }, [children, maxWidth, variant, singleLine])

    const TooltipText = ({
      children,
      tooltip,
    }: {
      children: ReactNode
      tooltip: string
    }) => {
      return (
        <Tooltip description={tooltip} tableTooltip>
          {children}
        </Tooltip>
      )
    }

    const StyledText = () => {
      const baseStyle: Style = {
        position: 'relative',
        textAlign: align,
        color: colorProp === 'inherit' ? 'inherit' : colors[colorProp],
        lineHeight: 'normal',
        overflowWrap: 'break-word',
        textRendering: 'optimizeLegibility',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        WebkitTextSizeAdjust: '100%',
        MozTextSizeAdjust: '100%',
        textSizeAdjust: '100%',
        userSelect: selectableText ? 'text' : 'none',
        maxWidth,
        whiteSpace: 'pre-line',
        ...TEXT_VARIANT_STYLES[variant],
        ...style,
      }

      const singleLineStyle: Style | undefined = singleLine
        ? {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }
        : undefined

      return (
        <styled.p
          ref={(node) => mergeRefs(node, innerRef, externalRef)}
          style={{
            ...baseStyle,
            ...singleLineStyle,
          }}
        >
          {renderChildrenWithLineBreaks(children, singleLine)}
        </styled.p>
      )
    }

    return tooltip ? (
      <TooltipText tooltip={tooltip}>
        <StyledText />
      </TooltipText>
    ) : (
      <StyledText />
    )
  },
)

type Chars = { [key: string]: number }

const memoizeMap: Map<number, Chars> = new Map()
let ctx: CanvasRenderingContext2D | null
let debugCanvas: HTMLCanvasElement | null = null

const drawChar = (
  char: string,
  { fontFamily, fontSize, fontWeight, letterSpacing }: CSSProperties,
): number => {
  if (typeof document !== 'undefined') {
    if (!debugCanvas) {
      debugCanvas = document.createElement('canvas')
      debugCanvas.style.position = 'fixed'
      debugCanvas.style.top = '80px'
      debugCanvas.style.right = '10px'
      debugCanvas.style.border = '1px solid red'
      debugCanvas.width = 200
      debugCanvas.height = 100
      document.body.appendChild(debugCanvas)
      ctx = debugCanvas.getContext('2d')
    }

    if (!debugCanvas || !ctx) {
      const numericSize = parseFloat(String(fontSize).replace('px', ''))
      return Number.isNaN(numericSize) ? 0 : numericSize
    }

    ctx.clearRect(0, 0, debugCanvas.width, debugCanvas.height)
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, debugCanvas.width, debugCanvas.height)
    ctx.letterSpacing = letterSpacing ? '1px' : '0px'

    const size = typeof fontSize === 'number' ? `${fontSize}px` : fontSize
    const weight = fontWeight ?? ''
    const family = fontFamily ?? 'sans-serif'

    ctx.font = `${weight} ${size} ${family}`
    ctx.fillStyle = 'black'
    ctx.fillText(char, 20, 50)
    const measured = ctx.measureText(char).width

    ctx.font = '10px Arial'
    ctx.fillText(`Font: ${ctx.font}`, 10, 80)
    ctx.fillText(`Char: ${char}`, 10, 90)
    ctx.fillText(`Width: ${measured}px`, 10, 20)

    return measured
  }

  const numericSize = parseFloat(String(fontSize).replace('px', ''))
  return Number.isNaN(numericSize) ? 0 : numericSize
}

const measureTextWidth = (text: string, variant: Typography) => {
  if (typeof document === 'undefined') {
    // fallback bem simples pra ambientes sem DOM
    return text.length * 10
  }

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const typography = TEXT_VARIANT_STYLES[variant]

  if (!ctx) return 0

  const fontSize = String(typography.fontSize).replace('px', '')
  const lineHeight = String(typography.lineHeight).replace('px', '')
  const fontFamily = typography.fontFamily
  const fontWeight = typography.fontWeight

  ctx.font = `${fontWeight} ${fontSize}px/${lineHeight}px ${fontFamily}`

  const baseWidth = ctx.measureText(text).width
  const letterSpacing = parseFloat(
    String(typography.letterSpacing).replace('px', ''),
  )

  const adjusted =
    baseWidth +
    (Number.isNaN(letterSpacing) ? 0 : letterSpacing * (text.length - 1))

  return Math.ceil(adjusted)
}

const MemorizedText = memo(Text)

export type { TextProps }
export { MemorizedText as Text, measureTextWidth }
